<script>
	// The params prop contains values matched from the URL
	export let params = {};
	
	import { replace, push } from 'svelte-spa-router';
	import { createEventDispatcher, onMount } from 'svelte';
	import TBoxInterno from '../templates/TBoxInterno.svelte';
	import BarraOperacoesRegistro from '../../componentes/BarraOperacoesRegistro.svelte';
	import { 
		API, 
		TipoEvento,
		StatusHttp, 
		cabecalhoPadraoRequisicao,
		dadosUsuarioLS, 
		TextoFeedback,
		TipoPerfil, 
        removerObjeto } from '../../utils';
	import { sTituloPaginaAtual } from '../../stores';

	const dispatch = createEventDispatcher();
	let dadosUsuario;
	let opcoesOperacoes = [];
	
	let avaliacao = {};
	let objetoAvaliado = {};
	let afirmacoes = {};
	let anexos = [];
	let tituloPagina = "...";
	let subtituloPagina = "...";
	let somatorioPontucao = 0;
	let totalAfirmacoes = 0;
	const AJUSTE_LIKERT = 3;

	const itensAvaliacaoLikert = [
		'Discordo totalmente', 
		'Discordo parcialmente',
		'Não discordo nem concordo',
		'Concordo parcialmente',
		'Concordo totalmente'
	];

	onMount(async () => {
		$sTituloPaginaAtual = `Visualização de Avaliação`;

		dispatch('routeEvent', {
			tipo: TipoEvento.ESTA_CARREGANDO,
			valor: true,
			mensagem: 'Carregando avaliação ...',
		});

		const resposta = await fetch(API(`avaliacoes/${params.id}`), {
			method: 'GET',
			headers: cabecalhoPadraoRequisicao()
		}).catch(erros => {
			dispatch('routeEvent', {
				tipo: TipoEvento.ABRIR_MODAL,
				titulo: 'Erro',
				conteudo: TextoFeedback.ERRO_REQUISICAO
			});

			replace("#/painel");
		});
		
		const { dados } = await resposta.json();

		if (resposta.status == StatusHttp.OK) {
			objetoAvaliado = dados.objetoAvaliado;
			afirmacoes = dados.afirmacoes;
			anexos = objetoAvaliado?.anexos;
			tituloPagina = objetoAvaliado?.titulo ?? objetoAvaliado?.projeto?.titulo;
			subtituloPagina = `Tipo de objeto avaliado: ${dados.objetoAvaliadoEProjeto == true ? 'PROJETO' : 'APRESENTAÇÃO SEM. GRADUAÇÃO'}`;
			
			avaliacao = {
				pontuacao: dados.pontuacao,
				anotacoes: dados.anotacoes,
				finalizada: dados.finalizada,
				data_ultima_atualizacao: dados.ultima_atualizacao,
				renovacao: dados.renovacao,
			};

			dadosUsuario = dadosUsuarioLS();

			opcoesOperacoes = [
				{
					titulo: 'Início',
					callback: () => push('#/')
				},
				{
					titulo: 'Painel',
					callback: () => push('#/painel'),
				},
				{
					titulo: 'Formulário de Recurso',
					callback: () => window.open('http://www.cetreina.uerj.br/documentos/Formulario_Recurso__Cetreina_2024.docx', "_blank"),
					cor: 'is-danger'
				}
			];
		} else {
			dispatch('routeEvent', { 
				tipo: TipoEvento.ESTA_CARREGANDO,
				valor: false
			});
			
			replace('#/avaliacao-inexistente');

			return;
		}

		somatorioPontucao = afirmacoes.reduce((total, afirmacao) => {
			return afirmacao.intencao == 'POSITIVA' 
				? total + (afirmacao.pontuacao - AJUSTE_LIKERT) 
				: total + (afirmacao.pontuacao - AJUSTE_LIKERT) * (-1)
		}, 0);

		totalAfirmacoes = Object.values(afirmacoes).length;

		dispatch('routeEvent', { 
			tipo: TipoEvento.ESTA_CARREGANDO,
			valor: false
		});
	});
</script>

<TBoxInterno
	titulo={tituloPagina}
	subtitulo={subtituloPagina}
	largura="12" 
	deslocamento="0">

	<div class="columns">
		<div class="column">
			<div class="notification is-primary is-light">
				<strong>Cálculo da pontuação do projeto/apresentação:</strong>
				<br>
				<p>Deve-se realizar o somatório de cada item avaliado e dividir pelo total de itens do formulário. Ao final, soma-se o valor 3, que é a constante de ajuste da <u>escala likert</u> (5 níveis).</p>
				<br>
				<p>A <u>pontuação máxima</u> que um(a) projeto/apresentação pode receber é 5,000.</p>
				
				<p>O mecanismo de intenção avaliativa objetiva inverter a escala e mitigar o <u>viés de aquiescência</u>.</p>
				
				<p>Para auxiliar na redução de empates, a pontuação utiliza três casas decimais.</p>

				<br>

				<strong>Cálculo da pontuação final</strong>
				<p>A pontuação final é composta pela média da pontuação do projeto + apresentação da Semana de Graduação 2023.</p>
			</div>
		</div>
	</div>

	<hr>

	<p class="is-size-4 mb-4">Avaliação:</p>
	
	<div class="content">
		<table class="table is-fullwidth is-hoverable">
			<thead>
				<tr>
					<th>#</th>
					<th>Item avaliado</th>
					<th>Intenção avaliativa</th>
					<th title="Avaliação do item atribuída pelo(a) avaliador(a)">Avaliação do item</th>
					<th>Pontos</th>
				</tr>
			</thead>
			<tbody>
				{#each Object.values(afirmacoes) as afirmacao, i}
				<tr>
					<td>{ ++i }</td> 
					<td>{ afirmacao.titulo }</td>
					<td>{ afirmacao.intencao }</td>
					<td>{ itensAvaliacaoLikert[afirmacao.pontuacao - 1] }</td>
					<td class="has-text-right">{ afirmacao.intencao == 'POSITIVA' ? (afirmacao.pontuacao - AJUSTE_LIKERT) : (afirmacao.pontuacao - AJUSTE_LIKERT) * (-1) }</td>
				</tr>
				{:else}
				<td colspan="5" class="has-text-danger has-text-centered">Avaliação ainda não finalizada.</td>
				{/each}
			</tbody>
		</table>

		<hr>

		<p class="is-size-4 mb-4">Pontuação:</p>

		<div class="columns">
			<div class="column is-3">
				<div class="notification is-warning">
					<p>Total: <strong>{ avaliacao.pontuacao }</strong></p>
				</div>
			</div>
			<div class="column is-9">
				<div class="notification is-info is-light has-text-right">
					<p>
						<strong>Somatório</strong>: { somatorioPontucao } | 
						<strong>Total de afirmações</strong>: { totalAfirmacoes } | 
						<strong>Pontuação da avaliação</strong>: { parseFloat((somatorioPontucao / totalAfirmacoes)).toFixed(3) } + 3
					</p>
				</div>
			</div>
		</div>

		<hr>

		<p class="is-size-4 mb-4">Anotações do(a) avaliador(a)</p>
		
		<div class="box">
			{ avaliacao.anotacoes }
		</div>
	</div>

	<p class="has-text-centered">
		<span class="has-text-info">Avaliador(a): ******</span>
		<br>
		<span class="tag" title="Data da avaliação">{avaliacao.data_ultima_atualizacao}</span>
	</p>
	
	<br>
	<br>

	<BarraOperacoesRegistro opcoes={ opcoesOperacoes } />
</TBoxInterno>